import { create } from "zustand";
import type { DictionaryEntry } from "@/types/dictionary";
import dictionaryData from "@/data/dictionary.json";

interface DictionaryStore {
  // Popup state
  isOpen: boolean;
  entry: DictionaryEntry | null;
  loading: boolean;
  error: string | null;

  // Recent lookups
  recentWords: DictionaryEntry[];

  // Actions
  lookupWord: (word: string) => Promise<DictionaryEntry | null>;
  openPopup: (word: string) => Promise<void>;
  closePopup: () => void;
  clearError: () => void;
  addToRecent: (entry: DictionaryEntry) => void;
  clearRecent: () => void;
}

const dictionaryEntries = (Array.isArray(dictionaryData.words)
  ? dictionaryData.words
  : []) as DictionaryEntry[];

// Build a fast lookup index for both surface and base forms.
const dictionaryIndex = (() => {
  const index = new Map<string, DictionaryEntry>();

  const addEntry = (key: string | undefined, entry: DictionaryEntry) => {
    if (!key) return;
    const normalized = key.toLowerCase();
    if (!index.has(normalized)) {
      index.set(normalized, entry);
    }
  };

  for (const entry of dictionaryEntries) {
    if (!entry || typeof entry !== "object") continue;
    addEntry(entry.word_in_context, entry);
    addEntry(entry.word, entry);
  }

  return index;
})();

const lookupEntry = (word: string): DictionaryEntry | null => {
  if (!word) return null;
  return dictionaryIndex.get(word.toLowerCase()) ?? null;
};

export const useDictionaryStore = create<DictionaryStore>((set, get) => ({
  isOpen: false,
  entry: null,
  loading: false,
  error: null,
  recentWords: [],

  // Hover tooltip için kelime arama (popup açmadan)
  lookupWord: async (word: string): Promise<DictionaryEntry | null> => {
    try {
      // Simüle edilmiş API çağrısı
      await new Promise((resolve) => setTimeout(resolve, 150));

      return lookupEntry(word);
    } catch {
      return null;
    }
  },

  openPopup: async (word: string) => {
    set({ loading: true, error: null });

    try {
      // Simüle edilmiş API çağrısı
      await new Promise((resolve) => setTimeout(resolve, 300));

      const entry = lookupEntry(word);

      if (entry) {
        set({
          isOpen: true,
          entry,
          loading: false,
        });
        get().addToRecent(entry);
      } else {
        set({
          isOpen: true,
          entry: null,
          loading: false,
          error: `"${word}" kelimesi sözlükte bulunamadı`,
        });
      }
    } catch {
      set({
        loading: false,
        error: "Sözlük hatası",
      });
    }
  },

  closePopup: () => set({ isOpen: false, entry: null, error: null }),

  clearError: () => set({ error: null }),

  addToRecent: (entry) => {
    const { recentWords } = get();
    const filtered = recentWords.filter((w) => w.id !== entry.id);
    set({
      recentWords: [entry, ...filtered].slice(0, 10),
    });
  },

  clearRecent: () => set({ recentWords: [] }),
}));
